import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  getManagedVenue, getAvailability, submitBooking, previewCost, previewSettlement,
  houseLabel, BROADCAST_PRICES, MARKETING_PRICES,
  type ManagedVenueDetail, type BookingDraft, type BroadcastPackage, type MarketingPackage,
} from '../../lib/managedVenues';
import { ApiError } from '../../lib/apiClient';
import { formatMoney } from '../../lib/format';
import './ManagedVenues.css';
import './VenueWizard.css';

/**
 * The eight step intake.
 *
 * OPEN TO ANYONE. A promoter with no Voleto account can complete this end to end; the
 * engine attaches their user id only if they happen to be signed in. That is why nothing
 * here assumes an account and why the confirmation hands back a booking id, which is the
 * only key to the pipeline afterwards.
 *
 * THE SIDEBAR TOTAL IS A PREVIEW. It exists so an organizer can see the consequence of
 * each choice while making it. The engine recalculates from the rate card on submit and
 * stores its own figure, so this number informs a decision and never determines a charge.
 */

const STEPS = [
  'Event details',
  'Venue and time',
  'Admission',
  'Production',
  'Marketing',
  'Audio and stage',
  'Insurance',
  'Review',
];

const EVENT_TYPES = ['Concert', 'Theatre', 'Comedy', 'Conference', 'Recital', 'Community', 'Other'];

const todayIso = () => new Date().toISOString().slice(0, 10);

function emptyDraft(capacity: number): BookingDraft {
  return {
    organizerName: '', organizerEmail: '',
    eventName: '', eventType: 'Concert',
    eventStartDate: '', eventEndDate: '',
    attendance: Math.min(200, capacity),
    extraHours: 0,
    admissionType: 'free', ticketPriceCents: 0, rsvpCap: capacity,
    technicianCount: 1, broadcastPackage: null, ledWall: false, projector: false,
    marketingPackage: null,
    inputList: '', stagePlotUrl: '', coiUrl: '', coiCoverageDays: 2,
    exclusivityAgreed: false,
  };
}

/** A month grid with past and reserved days disabled. */
function DatePicker({ slug, value, onPick }: {
  slug: string; value: { start: string; end: string }; onPick: (start: string, end: string) => void;
}) {
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getUTCFullYear(), m: now.getUTCMonth() + 1 });
  const [blocked, setBlocked] = useState<Set<string>>(new Set());

  useEffect(() => {
    getAvailability(slug, cursor.y, cursor.m)
      .then((a) => setBlocked(new Set(a.blocked.map((b) => b.date))))
      .catch(() => setBlocked(new Set()));
  }, [slug, cursor]);

  const days = useMemo(() => {
    const first = new Date(Date.UTC(cursor.y, cursor.m - 1, 1));
    const lead = first.getUTCDay();
    const count = new Date(Date.UTC(cursor.y, cursor.m, 0)).getUTCDate();
    const cells: Array<string | null> = Array.from({ length: lead }, () => null);
    for (let d = 1; d <= count; d += 1) {
      cells.push(`${cursor.y}-${String(cursor.m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    }
    return cells;
  }, [cursor]);

  const shift = (by: number) => setCursor((c) => {
    const d = new Date(Date.UTC(c.y, c.m - 1 + by, 1));
    return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1 };
  });

  function pick(date: string) {
    // First click sets the start; a later date extends the range; anything else restarts.
    if (!value.start || (value.start && value.end)) { onPick(date, date); return; }
    if (date >= value.start) onPick(value.start, date);
    else onPick(date, date);
  }

  const inRange = (d: string) => value.start && value.end && d >= value.start && d <= value.end;

  return (
    <div className="wz-cal">
      <div className="wz-cal__head">
        <button type="button" className="button button--ghost adm-btn-sm" onClick={() => shift(-1)}>
          Previous
        </button>
        <span className="wz-cal__month">
          {new Date(Date.UTC(cursor.y, cursor.m - 1, 1)).toLocaleString('en-US',
            { month: 'long', year: 'numeric', timeZone: 'UTC' })}
        </span>
        <button type="button" className="button button--ghost adm-btn-sm" onClick={() => shift(1)}>
          Next
        </button>
      </div>

      <div className="wz-cal__grid" role="grid">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <span key={`${d}${i}`} className="wz-cal__dow">{d}</span>
        ))}
        {days.map((d, i) => {
          if (!d) return <span key={`pad${i}`} />;
          const isPast = d < todayIso();
          const isBlocked = blocked.has(d);
          const disabled = isPast || isBlocked;
          return (
            <button
              key={d}
              type="button"
              className={`wz-cal__day${inRange(d) ? ' is-picked' : ''}${isBlocked ? ' is-blocked' : ''}`}
              disabled={disabled}
              title={isBlocked ? 'Already reserved' : undefined}
              onClick={() => pick(d)}
            >
              {Number(d.slice(8))}
            </button>
          );
        })}
      </div>
      <p className="wz-hint">
        Reserved and past dates cannot be selected. Click a start date, then an end date for
        a multi day booking.
      </p>
    </div>
  );
}

export function VenueBookingWizard() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState<ManagedVenueDetail | null>(null);
  const [step, setStep] = useState(0);
  // Capacity 0 until the venue loads. The form does not render before then (see the
  // `if (!venue)` guard below) and the draft is replaced from v.capacity the moment it
  // does, so this value is never shown. It used to be 550 — one venue's capacity acting
  // as every venue's starting point, which is the coupling migration 078 removed from
  // the schema.
  const [draft, setDraft] = useState<BookingDraft>(() => emptyDraft(0));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [estimatorCount, setEstimatorCount] = useState(0);

  useEffect(() => {
    getManagedVenue(slug)
      .then((v) => { setVenue(v); setDraft(emptyDraft(v.capacity)); setEstimatorCount(Math.floor(v.capacity / 2)); })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load this venue.'));
  }, [slug]);

  const set = useCallback(<K extends keyof BookingDraft>(k: K, v: BookingDraft[K]) => {
    setDraft((d) => ({ ...d, [k]: v }));
  }, []);

  const cost = venue ? previewCost(draft, venue.rateCard) : { lines: [], totalCents: 0 };
  const estimate = venue && draft.admissionType === 'ticketed'
    ? previewSettlement({
      ticketPriceCents: draft.ticketPriceCents, ticketCount: estimatorCount, rate: venue.rateCard })
    : null;

  /** What blocks the Next button on each step, and why. */
  function blocker(): string | null {
    if (!venue) return 'Loading';
    switch (step) {
      case 0:
        if (!draft.eventName.trim()) return 'Event name is required';
        if (!draft.organizerName.trim()) return 'Your name is required';
        if (!draft.organizerEmail.trim()) return 'Your email is required';
        if (!draft.eventStartDate) return 'Pick at least one date';
        if (draft.attendance > venue.capacity) return `This room holds ${venue.capacity}`;
        if (draft.attendance < 1) return 'Expected attendance is required';
        return null;
      case 2:
        if (draft.admissionType === 'ticketed' && draft.ticketPriceCents <= 0) {
          return 'Set a ticket price';
        }
        if (!draft.exclusivityAgreed) return 'Exclusive ticketing must be accepted';
        return null;
      case 6:
        if (!draft.coiUrl.trim()) return 'A certificate of insurance is required';
        return null;
      default:
        return null;
    }
  }
  const blocked = blocker();

  async function submit() {
    if (!venue) return;
    setSubmitting(true);
    setError(null);
    try {
      const booking = await submitBooking(slug, draft);
      navigate(`/venues/bookings/${booking.id}/pipeline`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not submit the request.');
      setSubmitting(false);
    }
  }

  if (error && !venue) {
    return <div className="mv-page"><div className="alert alert--error" role="alert">{error}</div></div>;
  }
  if (!venue) return <div className="mv-page"><p className="mv-state">Loading…</p></div>;

  const rate = venue.rateCard;

  return (
    <div className="wz-page">
      <header className="wz-head">
        <p className="wz-head__eyebrow mono">{venue.managingEntity} managed facility</p>
        <h1 className="wz-head__title display">{venue.name}</h1>
        <p className="wz-head__sub">
          {venue.address}, {venue.city}, {venue.state} · {venue.capacity} capacity
        </p>
      </header>

      <ol className="wz-steps">
        {STEPS.map((label, i) => (
          <li key={label}
            className={`wz-steps__item${i === step ? ' is-current' : ''}${i < step ? ' is-done' : ''}`}>
            <button type="button" className="wz-steps__btn"
              onClick={() => { if (i < step) setStep(i); }}>
              <span className="wz-steps__n mono">{i + 1}</span>
              <span className="wz-steps__label">{label}</span>
            </button>
          </li>
        ))}
      </ol>

      <div className="wz-layout">
        <section className="wz-panel">
          {error && <div className="alert alert--error" role="alert">{error}</div>}

          {/* 1 */}
          {step === 0 && (
            <>
              <h2 className="wz-panel__title">Event details and dates</h2>
              <div className="wiz-grid">
                <label className="field wiz-field--wide">
                  <span className="field__label">Event name *</span>
                  <input className="field__input" value={draft.eventName}
                    onChange={(e) => set('eventName', e.target.value)} />
                </label>
                <label className="field">
                  <span className="field__label">Event type *</span>
                  <select className="field__input" value={draft.eventType}
                    onChange={(e) => set('eventType', e.target.value)}>
                    {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label className="field">
                  <span className="field__label">Expected attendance *</span>
                  <input className="field__input mono" type="number" min={1} max={venue.capacity}
                    value={draft.attendance}
                    onChange={(e) => set('attendance', Number(e.target.value))} />
                </label>
                <label className="field">
                  <span className="field__label">Your name *</span>
                  <input className="field__input" value={draft.organizerName}
                    onChange={(e) => set('organizerName', e.target.value)} />
                </label>
                <label className="field">
                  <span className="field__label">Your email *</span>
                  <input className="field__input" type="email" value={draft.organizerEmail}
                    onChange={(e) => set('organizerEmail', e.target.value)} />
                </label>
              </div>

              {draft.attendance > venue.capacity && (
                <p className="wz-warn">
                  This room is licensed for {venue.capacity}. Reduce expected attendance to continue.
                </p>
              )}

              <DatePicker slug={slug}
                value={{ start: draft.eventStartDate, end: draft.eventEndDate }}
                onPick={(s, e) => { set('eventStartDate', s); set('eventEndDate', e); }} />
            </>
          )}

          {/* 2 */}
          {step === 1 && (
            <>
              <h2 className="wz-panel__title">Venue and time</h2>
              <p className="wz-hint">
                Every booking includes {rate.baseHours} hours of facility access at
                {' '}{formatMoney(rate.baseRentalCents)}. Extend in one hour steps at
                {' '}{formatMoney(rate.extraHourRateCents)} each.
              </p>
              <div className="wz-stepper">
                <button type="button" className="button button--ghost"
                  onClick={() => set('extraHours', Math.max(0, draft.extraHours - 1))}>-</button>
                <span className="wz-stepper__value mono">
                  {rate.baseHours + draft.extraHours} hours
                </span>
                <button type="button" className="button button--ghost"
                  onClick={() => set('extraHours', Math.min(8, draft.extraHours + 1))}>+</button>
              </div>
              <p className="wz-hint">
                {draft.extraHours} extended hour{draft.extraHours === 1 ? '' : 's'}, up to 8.
              </p>
            </>
          )}

          {/* 3 */}
          {step === 2 && (
            <>
              <h2 className="wz-panel__title">Admission and exclusivity</h2>
              <div className="wz-choices">
                <button type="button"
                  className={`wz-choice${draft.admissionType === 'free' ? ' is-picked' : ''}`}
                  onClick={() => { set('admissionType', 'free'); set('ticketPriceCents', 0); }}>
                  <strong>Free or RSVP</strong>
                  <span>No ticket revenue. Capacity managed by registration.</span>
                </button>
                <button type="button"
                  className={`wz-choice${draft.admissionType === 'ticketed' ? ' is-picked' : ''}`}
                  onClick={() => set('admissionType', 'ticketed')}>
                  <strong>Ticketed</strong>
                  <span>Sold through Voleto. Proceeds settle after the event.</span>
                </button>
              </div>

              {draft.admissionType === 'ticketed' && (
                <>
                  <label className="field">
                    <span className="field__label">Ticket price *</span>
                    <input className="field__input mono" type="number" min={1} step={1}
                      value={draft.ticketPriceCents / 100}
                      onChange={(e) => set('ticketPriceCents', Math.round(Number(e.target.value) * 100))} />
                  </label>

                  <div className="wz-estimator">
                    <h3 className="wz-estimator__title">Earnings estimate</h3>
                    <input className="wz-estimator__slider" type="range"
                      min={0} max={venue.capacity} value={estimatorCount}
                      onChange={(e) => setEstimatorCount(Number(e.target.value))} />
                    <p className="wz-estimator__house">
                      <span className="mono">{estimatorCount}</span> of {venue.capacity} sold
                      {' · '}{houseLabel(estimatorCount, venue.capacity)}
                    </p>
                    {estimate && (
                      <dl className="wz-estimator__rows">
                        <div><dt>Gross</dt><dd className="mono">{formatMoney(estimate.grossCents)}</dd></div>
                        <div><dt>Facility fee ({rate.schoolFeePct}%)</dt>
                          <dd className="mono">-{formatMoney(estimate.schoolFeeCents)}</dd></div>
                        <div><dt>{venue.managingEntity} fee ({rate.vyrtualFeePct}%)</dt>
                          <dd className="mono">-{formatMoney(estimate.vyrtualFeeCents)}</dd></div>
                        <div><dt>Card processing (2.9% + $0.30 per ticket)</dt>
                          <dd className="mono">-{formatMoney(estimate.stripeFeeCents)}</dd></div>
                        <div className="wz-estimator__net">
                          <dt>Net to you</dt><dd className="mono">{formatMoney(estimate.netCents)}</dd></div>
                      </dl>
                    )}
                    <p className="wz-hint">
                      An estimate at the sell through you choose. Actual settlement uses the
                      final counted admissions.
                    </p>
                  </div>
                </>
              )}

              <label className="wz-check">
                <input type="checkbox" checked={draft.exclusivityAgreed}
                  onChange={(e) => set('exclusivityAgreed', e.target.checked)} />
                <span>
                  <strong>Voleto is the exclusive ticketing channel for this event.</strong> All
                  admission is issued and scanned through Voleto, and no admission may be sold
                  elsewhere. Ticket proceeds are held in escrow and released after the event.
                </span>
              </label>
            </>
          )}

          {/* 4 */}
          {step === 3 && (
            <>
              <h2 className="wz-panel__title">Production staffing and equipment</h2>
              <p className="wz-hint">
                At least one technician is required. Each is {formatMoney(rate.techBaseRateCents)}
                {' '}plus {formatMoney(rate.techExtraHourRateCents)} per extended hour.
              </p>
              <div className="wz-stepper">
                <button type="button" className="button button--ghost"
                  onClick={() => set('technicianCount', Math.max(1, draft.technicianCount - 1))}>-</button>
                <span className="wz-stepper__value mono">{draft.technicianCount} technicians</span>
                <button type="button" className="button button--ghost"
                  onClick={() => set('technicianCount', Math.min(6, draft.technicianCount + 1))}>+</button>
              </div>

              <h3 className="wz-sub">Broadcast</h3>
              <div className="wz-choices">
                {(Object.keys(BROADCAST_PRICES) as BroadcastPackage[]).map((k) => (
                  <button key={k} type="button"
                    className={`wz-choice${draft.broadcastPackage === k ? ' is-picked' : ''}`}
                    onClick={() => set('broadcastPackage', draft.broadcastPackage === k ? null : k)}>
                    <strong>{k[0].toUpperCase() + k.slice(1)}</strong>
                    <span className="mono">{formatMoney(BROADCAST_PRICES[k])}</span>
                  </button>
                ))}
              </div>

              <h3 className="wz-sub">Equipment</h3>
              <label className="wz-check">
                <input type="checkbox" checked={draft.ledWall}
                  onChange={(e) => set('ledWall', e.target.checked)} />
                <span>LED wall <span className="mono">{formatMoney(rate.ledWallPriceCents)}</span></span>
              </label>
              <label className="wz-check">
                <input type="checkbox" checked={draft.projector}
                  onChange={(e) => set('projector', e.target.checked)} />
                <span>Projector <span className="mono">{formatMoney(rate.projectorPriceCents)}</span></span>
              </label>
            </>
          )}

          {/* 5 */}
          {step === 4 && (
            <>
              <h2 className="wz-panel__title">Marketing</h2>
              <p className="wz-hint">Optional. Skip to continue without a package.</p>
              <div className="wz-choices">
                {(Object.keys(MARKETING_PRICES) as MarketingPackage[]).map((k) => (
                  <button key={k} type="button"
                    className={`wz-choice${draft.marketingPackage === k ? ' is-picked' : ''}`}
                    onClick={() => set('marketingPackage', draft.marketingPackage === k ? null : k)}>
                    <strong>{k[0].toUpperCase() + k.slice(1)}</strong>
                    <span className="mono">{formatMoney(MARKETING_PRICES[k])}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* 6 */}
          {step === 5 && (
            <>
              <h2 className="wz-panel__title">Audio and stage</h2>
              <label className="field wiz-field--wide">
                <span className="field__label">Input list</span>
                <textarea className="field__input wiz-textarea" rows={6} value={draft.inputList}
                  placeholder="Channel list, backline, monitor requirements"
                  onChange={(e) => set('inputList', e.target.value)} />
              </label>
              <label className="field wiz-field--wide">
                <span className="field__label">Stage plot URL</span>
                <input className="field__input" type="url" value={draft.stagePlotUrl}
                  placeholder="Link to a PDF, JPG or PNG"
                  onChange={(e) => set('stagePlotUrl', e.target.value)} />
              </label>
              <p className="wz-hint">
                Voleto stores links rather than hosting uploads, so paste a link to a hosted
                file. Both are optional now and required before the technical prep stage.
              </p>
            </>
          )}

          {/* 7 */}
          {step === 6 && (
            <>
              <h2 className="wz-panel__title">Insurance</h2>
              <table className="wz-table">
                <tbody>
                  <tr><th scope="row">Each occurrence</th><td className="mono">$1,000,000</td></tr>
                  <tr><th scope="row">General aggregate</th><td className="mono">$2,000,000</td></tr>
                  <tr><th scope="row">Form</th><td>ACORD 25</td></tr>
                </tbody>
              </table>

              <div className="wz-holder">
                <h3 className="wz-sub">Certificate holder</h3>
                <p className="mono">{venue.managingEntity}</p>
                <p className="mono">{venue.name}</p>
                <p className="mono">{venue.address}, {venue.city}, {venue.state}</p>
                <p className="wz-hint">
                  Name both the facility and {venue.managingEntity} as additional insureds.
                </p>
              </div>

              <div className="wz-stepper">
                <button type="button" className="button button--ghost"
                  onClick={() => set('coiCoverageDays', Math.max(1, draft.coiCoverageDays - 1))}>-</button>
                <span className="wz-stepper__value mono">{draft.coiCoverageDays} days coverage</span>
                <button type="button" className="button button--ghost"
                  onClick={() => set('coiCoverageDays', Math.min(30, draft.coiCoverageDays + 1))}>+</button>
              </div>
              <p className="wz-hint">Must cover load in and load out, not only the event day.</p>

              <label className="field wiz-field--wide">
                <span className="field__label">Certificate URL *</span>
                <input className="field__input" type="url" value={draft.coiUrl}
                  placeholder="Link to your ACORD 25 certificate"
                  onChange={(e) => set('coiUrl', e.target.value)} />
              </label>
            </>
          )}

          {/* 8 */}
          {step === 7 && (
            <>
              <h2 className="wz-panel__title">Review</h2>
              <dl className="wz-review">
                {[
                  ['Event', `${draft.eventName} (${draft.eventType})`, 0],
                  ['Dates', draft.eventStartDate === draft.eventEndDate
                    ? draft.eventStartDate
                    : `${draft.eventStartDate} to ${draft.eventEndDate}`, 0],
                  ['Attendance', String(draft.attendance), 0],
                  ['Hours', `${rate.baseHours + draft.extraHours}`, 1],
                  ['Admission', draft.admissionType === 'ticketed'
                    ? `Ticketed at ${formatMoney(draft.ticketPriceCents)}` : 'Free or RSVP', 2],
                  ['Technicians', String(draft.technicianCount), 3],
                  ['Broadcast', draft.broadcastPackage ?? 'None', 3],
                  ['Equipment', [draft.ledWall && 'LED wall', draft.projector && 'Projector']
                    .filter(Boolean).join(', ') || 'None', 3],
                  ['Marketing', draft.marketingPackage ?? 'None', 4],
                  ['Insurance', draft.coiUrl ? 'Certificate attached' : 'Not attached', 6],
                ].map(([label, value, jump]) => (
                  <div key={String(label)}>
                    <dt>{label}</dt>
                    <dd>
                      {value}
                      <button type="button" className="wz-review__edit"
                        onClick={() => setStep(Number(jump))}>Edit</button>
                    </dd>
                  </div>
                ))}
              </dl>

              <p className="wz-hint">
                Submitting reserves your dates and sends the request for review. The final
                price is confirmed by {venue.managingEntity} before any payment is taken.
              </p>
            </>
          )}

          <div className="wz-actions">
            {step > 0 && (
              <button type="button" className="button button--ghost"
                onClick={() => setStep(step - 1)}>Back</button>
            )}
            {step < STEPS.length - 1 ? (
              <button type="button" className="button button--primary"
                disabled={Boolean(blocked)} title={blocked ?? undefined}
                onClick={() => setStep(step + 1)}>
                {blocked ?? 'Continue'}
              </button>
            ) : (
              <button type="button" className="button button--primary"
                disabled={submitting} onClick={() => void submit()}>
                {submitting ? 'Submitting…' : 'Submit booking request'}
              </button>
            )}
          </div>
        </section>

        <aside className="wz-sidebar">
          <h2 className="wz-sidebar__title">Your booking</h2>
          <dl className="wz-sidebar__lines">
            {cost.lines.map((l) => (
              <div key={l.key}>
                <dt>{l.label}</dt>
                <dd className="mono">{formatMoney(l.amountCents)}</dd>
              </div>
            ))}
          </dl>
          <div className="wz-sidebar__total">
            <span>Due before the event</span>
            <span className="mono">{formatMoney(cost.totalCents)}</span>
          </div>
          {estimate && (
            <p className="wz-sidebar__net">
              Projected net from ticket sales at this sell through:
              {' '}<span className="mono">{formatMoney(estimate.netCents)}</span>
            </p>
          )}
          <p className="wz-hint">
            Confirmed by {venue.managingEntity} before payment. Ticket proceeds settle
            separately, after the event.
          </p>
        </aside>
      </div>
    </div>
  );
}
